/**
 * PageTabs - Enterprise-grade page navigation component
 *
 * A comprehensive tabbed page layout component that provides:
 * - Page header with icon and optional subtitle
 * - Professional tabbed navigation with icons
 * - Role-based tab visibility
 * - Responsive design (mobile horizontal scroll, desktop inline)
 * - Keyboard navigation support
 * - Smooth transitions and hover states
 * - Badge and count indicators
 * - Optional action buttons in header
 *
 * @example
 * ```tsx
 * <PageTabs
 *   title="Expenses"
 *   subtitle="Manage your expense claims"
 *   icon={<CurrencyDollarIcon className="h-7 w-7" />}
 *   tabs={[
 *     { id: 'cash', label: 'Cash Expenses', icon: <BanknotesIcon /> },
 *     { id: 'corporate', label: 'Corporate Card', icon: <CreditCardIcon />, count: 5 },
 *     { id: 'admin', label: 'Administration', icon: <Cog6ToothIcon />, requiredRoles: ['admin'] },
 *   ]}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   userRoles={userRoles}
 *   headerActions={<Button>Export</Button>}
 * >
 *   {activeTab === 'cash' && <CashExpenses />}
 *   {activeTab === 'corporate' && <CorporateExpenses />}
 * </PageTabs>
 * ```
 */
import { ReactNode, useRef, useEffect, useState, useCallback, useMemo, KeyboardEvent } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import Button from './Button';

export interface PageTab {
  /** Unique identifier for the tab */
  id: string;
  /** Display label for the tab */
  label: string;
  /** Optional icon (React node, e.g., HeroIcon) */
  icon?: ReactNode;
  /** Optional count badge (e.g., number of items) */
  count?: number;
  /** Optional text badge (e.g., "New", "Beta") */
  badge?: string;
  /** Badge variant for styling */
  badgeVariant?: 'default' | 'warning' | 'error' | 'success' | 'info';
  /** Roles required to see this tab (if empty/undefined, visible to all) */
  requiredRoles?: string[];
  /** Whether the tab is disabled */
  disabled?: boolean;
  /** Optional tooltip text */
  tooltip?: string;
}

export interface PageTabsProps {
  /** Page title displayed in header */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Icon displayed next to title */
  icon?: ReactNode;
  /** Icon color class (defaults to text-blue-600) */
  iconColorClass?: string;
  /** Array of tab definitions */
  tabs: PageTab[];
  /** Currently active tab ID */
  activeTab: string;
  /** Callback when tab changes */
  onTabChange: (tabId: string) => void;
  /** Current user's roles for role-based tab visibility */
  userRoles?: string[];
  /** Optional actions to display in header (e.g., buttons) */
  headerActions?: ReactNode;
  /** Tab content (children) */
  children?: ReactNode;
  /** Visual variant for tabs */
  variant?: 'underline' | 'pills' | 'boxed';
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show the header section */
  showHeader?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Additional className for the content area */
  contentClassName?: string;
  /** Whether tabs should be full width on mobile */
  mobileFullWidth?: boolean;
  /** Whether to persist active tab in URL */
  persistInUrl?: boolean;
  /** URL parameter name for tab persistence */
  urlParam?: string;
}

/**
 * Check if user has any of the required roles
 */
function hasRequiredRole(userRoles: string[] = [], requiredRoles: string[] = []): boolean {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  const normalizedUserRoles = userRoles.map((r) => r.toLowerCase());
  return requiredRoles.some((role) => normalizedUserRoles.includes(role.toLowerCase()));
}

export default function PageTabs({
  title,
  subtitle,
  icon,
  iconColorClass = 'text-blue-600',
  tabs,
  activeTab,
  onTabChange,
  userRoles = [],
  headerActions,
  children,
  variant = 'underline',
  size = 'md',
  showHeader = true,
  className = '',
  contentClassName = '',
  mobileFullWidth = false,
}: PageTabsProps) {
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Filter tabs based on user roles
  const visibleTabs = tabs.filter((tab) => hasRequiredRole(userRoles, tab.requiredRoles));

  // Track scroll position and container resize for chevron indicators
  const updateScrollState = useCallback(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, visibleTabs]);

  const scrollBy = useCallback((direction: 'left' | 'right') => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  // Scroll active tab into view on mobile
  useEffect(() => {
    if (activeTabRef.current && tabsContainerRef.current) {
      const container = tabsContainerRef.current;
      const activeButton = activeTabRef.current;
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();

      // Check if button is outside visible area
      if (buttonRect.left < containerRect.left || buttonRect.right > containerRect.right) {
        activeButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeTab]);

  // Keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    const enabledTabs = visibleTabs.filter((t) => !t.disabled);
    const currentEnabledIndex = enabledTabs.findIndex((t) => t.id === visibleTabs[currentIndex].id);

    let newIndex = currentEnabledIndex;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = currentEnabledIndex > 0 ? currentEnabledIndex - 1 : enabledTabs.length - 1;
        break;
      case 'ArrowRight':
        e.preventDefault();
        newIndex = currentEnabledIndex < enabledTabs.length - 1 ? currentEnabledIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = enabledTabs.length - 1;
        break;
      default:
        return;
    }

    const newTab = enabledTabs[newIndex];
    if (newTab) {
      onTabChange(newTab.id);
    }
  };

  // Size classes
  const sizeClasses = {
    sm: {
      header: 'px-4 py-3',
      title: 'text-xl',
      subtitle: 'text-xs',
      icon: 'h-6 w-6',
      tab: 'px-3 py-2 text-xs',
      tabIcon: 'h-4 w-4',
      count: 'text-2xs min-w-[1.25rem] h-4',
    },
    md: {
      header: 'px-4 sm:px-6 py-4',
      title: 'text-2xl sm:text-3xl',
      subtitle: 'text-sm',
      icon: 'h-7 w-7 sm:h-8 sm:w-8',
      tab: 'px-3 sm:px-4 py-2.5 sm:py-3 text-sm',
      tabIcon: 'h-4 w-4 sm:h-5 sm:w-5',
      count: 'text-xs min-w-[1.5rem] h-5',
    },
    lg: {
      header: 'px-6 sm:px-8 py-5',
      title: 'text-3xl sm:text-4xl',
      subtitle: 'text-base',
      icon: 'h-8 w-8 sm:h-10 sm:w-10',
      tab: 'px-4 sm:px-6 py-3 sm:py-4 text-base',
      tabIcon: 'h-5 w-5 sm:h-6 sm:w-6',
      count: 'text-sm min-w-[1.75rem] h-6',
    },
  };

  // Variant classes
  const variantClasses = {
    underline: {
      container: 'border-b border-gray-200 bg-white',
      tabsWrapper: 'border-b-2 border-gray-200',
      tab: 'border-b-2 -mb-[2px] border-transparent transition-all duration-200',
      active: 'border-blue-600 text-blue-600 bg-blue-50/50',
      inactive: 'text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50',
      disabled: 'text-gray-400 cursor-not-allowed',
    },
    pills: {
      container: 'bg-white',
      tabsWrapper: 'bg-gray-100 p-1 rounded-lg inline-flex',
      tab: 'rounded-md transition-all duration-200',
      active: 'bg-white text-blue-600 shadow-sm font-semibold',
      inactive: 'text-gray-600 hover:text-gray-900 hover:bg-white/50',
      disabled: 'text-gray-400 cursor-not-allowed',
    },
    boxed: {
      container: 'bg-white',
      tabsWrapper: 'bg-gray-50 border border-gray-200 rounded-lg p-1 inline-flex',
      tab: 'rounded-md transition-all duration-200 border border-transparent',
      active: 'bg-white text-blue-600 shadow-sm border-gray-200 font-semibold',
      inactive: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
      disabled: 'text-gray-400 cursor-not-allowed',
    },
  };

  const styles = variantClasses[variant];
  const sizes = sizeClasses[size];

  // Badge variant colors
  const badgeColors = {
    default: 'bg-gray-100 text-gray-700',
    warning: 'bg-amber-100 text-amber-800',
    error: 'bg-red-100 text-red-800',
    success: 'bg-green-100 text-green-800',
    info: 'bg-blue-100 text-blue-800',
  };

  return (
    <div className={`min-h-full bg-gray-50 ${className}`}>
      {/* Header Section */}
      {showHeader && (
        <div className={`bg-white border-b border-gray-200 ${sizes.header}`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {icon && (
                <div className={`flex-shrink-0 ${iconColorClass}`}>
                  <div className={sizes.icon}>{icon}</div>
                </div>
              )}
              <div className="min-w-0">
                <h1 className={`font-bold text-gray-900 truncate ${sizes.title}`}>{title}</h1>
                {subtitle && (
                  <p className={`text-gray-500 mt-0.5 truncate ${sizes.subtitle}`}>{subtitle}</p>
                )}
              </div>
            </div>
            {headerActions && <div className="flex-shrink-0 flex items-center gap-2">{headerActions}</div>}
          </div>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className={`${styles.container} sticky top-0 z-10 shadow-sm relative max-w-full overflow-hidden`}>
        {/* Left caret scroll button */}
        {canScrollLeft && (
          <Button variant="outline" onClick={() => scrollBy('left')} aria-label="Scroll tabs left" className="absolute left-0 top-0 bottom-0 z-10">
            <ChevronLeftIcon className="h-5 w-5 text-blue-600" />
          </Button>
        )}

        {/* Right caret scroll button */}
        {canScrollRight && (
          <Button variant="outline" onClick={() => scrollBy('right')} aria-label="Scroll tabs right" className="absolute right-0 top-0 bottom-0 z-10">
            <ChevronRightIcon className="h-5 w-5 text-blue-600" />
          </Button>
        )}

        <div
          ref={tabsContainerRef}
          className={`
            overflow-x-auto scrollbar-hide
            ${variant === 'underline' ? '' : 'p-2 sm:p-3'}
          `}
        >
          <nav
            className={`
              flex gap-1
              ${variant === 'underline' ? 'px-4 sm:px-6' : ''}
              ${styles.tabsWrapper}
              ${mobileFullWidth ? 'w-full' : 'w-max sm:w-auto'}
            `}
            role="tablist"
            aria-label={`${title} navigation`}
          >
            {visibleTabs.map((tab, index) => {
              const isActive = tab.id === activeTab;
              const isDisabled = tab.disabled;

              return (
                <button
                  key={tab.id}
                  ref={isActive ? activeTabRef : null}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`tabpanel-${tab.id}`}
                  aria-disabled={isDisabled}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => !isDisabled && onTabChange(tab.id)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  title={tab.tooltip}
                  className={`
                    ${sizes.tab}
                    ${styles.tab}
                    ${isDisabled ? styles.disabled : isActive ? styles.active : styles.inactive}
                    ${mobileFullWidth ? 'flex-1' : ''}
                    flex items-center justify-center gap-2
                    whitespace-nowrap font-medium
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
                    select-none
                  `}
                >
                  {/* Tab Icon */}
                  {tab.icon && (
                    <span className={`flex-shrink-0 ${sizes.tabIcon} ${isActive ? '' : 'opacity-70'}`}>
                      {tab.icon}
                    </span>
                  )}

                  {/* Tab Label */}
                  <span>{tab.label}</span>

                  {/* Count Badge */}
                  {typeof tab.count === 'number' && (
                    <span
                      className={`
                        ${sizes.count}
                        inline-flex items-center justify-center
                        px-1.5 rounded-full font-semibold
                        ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}
                        transition-colors duration-200
                      `}
                    >
                      {tab.count > 99 ? '99+' : tab.count}
                    </span>
                  )}

                  {/* Text Badge */}
                  {tab.badge && (
                    <span
                      className={`
                        inline-flex items-center
                        px-2 py-0.5 text-2xs sm:text-xs font-medium rounded-full
                        ${badgeColors[tab.badgeVariant || 'default']}
                      `}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        className={`${contentClassName}`}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Helper hook to get user roles from localStorage. Memoised once per
 * mount so the returned array reference is stable across re-renders —
 * callers safely use it as a useMemo / useEffect / useCallback
 * dependency without causing infinite render loops. localStorage
 * doesn't change during a session, so a single read on mount is
 * sufficient.
 */
function readRolesFromStorage(): string[] {
  try {
    const userString = localStorage.getItem('user') || '{}';
    const user = JSON.parse(userString);
    const roles = new Set<string>();

    // Extract from _computedRoles
    if (Array.isArray(user._computedRoles)) {
      user._computedRoles.forEach((r: string) => r && roles.add(String(r).toLowerCase()));
    }

    // Extract from roles array
    if (Array.isArray(user.roles)) {
      user.roles.forEach((r: string | { name?: string }) => {
        if (typeof r === 'string') roles.add(r.toLowerCase());
        else if (r?.name) roles.add(String(r.name).toLowerCase());
      });
    }

    // Extract from dbUser.roles
    if (user.dbUser?.roles) {
      user.dbUser.roles.forEach((r: { name?: string }) => r?.name && roles.add(String(r.name).toLowerCase()));
    }

    // Extract from legacy role field
    if (user.role) roles.add(String(user.role).toLowerCase());

    return Array.from(roles);
  } catch {
    return [];
  }
}

export function useUserRoles(): string[] {
  return useMemo(() => readRolesFromStorage(), []);
}
